import {
  ExerciseTagDto,
  IExerciseAssetsCountDto,
  NotificationExerciseBriefDto,
} from "@growth-machine-llc/stridist-api-client";
import { useUpdateLibraryExercise } from "./useUpdateLibraryExercise";
import { Exercise, Options } from "./useSaveExerciseLibraryAsset";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { NOTIFICATIONS_MENU_LIST_QUERY_KEY } from "../../menu/NotificationsMenu";
import { COACH_EXERCISE_LIBRARY_ASSETS_COUNT_QUERY_KEY } from "../../../wrappers/router/coach/CoachExerciseLibraryWrapper";
import { getExerciseAssetsCountFromAction } from "../../../contexts/CoachTabsContext";
import { useUpdateNotificationsCounter } from "../../../hooks/useUpdateCurrentUser";

const APPROVE_EXERCISE_MESSAGE = "Exercise approved.";

// Update notification cache on exercise approval/archive/delete
// No need to process read notifications using mutations because on suggested asset processing it will be handled automatically
export const updateNotificationCacheOnExerciseApprovalActions = (
  queryClient: QueryClient,
  updateNotificationsCounter: (
    action: "increment" | "decrement" | "reset",
    type?:
      | "notificationUnreadCount"
      | "unreadActivitiesCount"
      | "unreadMessagesCount",
  ) => void,
  notification?: NotificationExerciseBriefDto,
) => {
  queryClient.invalidateQueries({
    queryKey: [NOTIFICATIONS_MENU_LIST_QUERY_KEY],
  });

  // Update pending assets count
  queryClient.setQueryData<IExerciseAssetsCountDto>(
    [COACH_EXERCISE_LIBRARY_ASSETS_COUNT_QUERY_KEY],
    (prev) => (prev ? getExerciseAssetsCountFromAction(prev, "approve") : prev),
  );
  if (!notification?.read) {
    updateNotificationsCounter("decrement", "notificationUnreadCount");
  }
};

export function useApprovePendingExerciseLibraryAsset(
  exerciseTags?: ExerciseTagDto[],
  notification?: NotificationExerciseBriefDto,
  updateMessage: string = APPROVE_EXERCISE_MESSAGE,
) {
  const { mutate: updateAsset, isPending } = useUpdateLibraryExercise(
    exerciseTags,
    updateMessage,
    true,
  );

  const updateNotificationsCounter = useUpdateNotificationsCounter();

  const queryClient = useQueryClient();

  const approveExercise = (
    { id, exercise, bodyparts, movementPatterns, tagIds }: Exercise,
    options?: Options,
  ) => {
    const content = JSON.stringify(exercise);

    return updateAsset(
      {
        id,
        name: exercise.title,
        content,
        bodyparts,
        movementPatterns,
        tagIds,
        approvedByCoach: true,
      },
      {
        onSuccess: () => {
          options?.onCompleted?.();
          updateNotificationCacheOnExerciseApprovalActions(
            queryClient,
            updateNotificationsCounter,
            notification,
          );
        },
      },
    );
  };

  return {
    approveExercise,
    isPending,
  };
}
